import { useParams, Link } from 'react-router-dom';
import { articles, hotComments } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug && a.categorySlug === article?.categorySlug).slice(0, 4);
  const comment = hotComments.find((c) => c.articleSlug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        url: `https://17chigua.com/article/${slug}`,
        datePublished: article.publishedAt,
        dateModified: article.publishedAt,
        author: {
          '@type': 'Person',
          name: article.authorName,
          jobTitle: article.authorRole,
        },
        publisher: {
          '@type': 'Organization',
          name: '17吃瓜网',
        },
        image: article.imageUrl,
        articleSection: article.category,
        keywords: article.tags?.join(', '),
        isAccessibleForFree: true,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, item: { '@id': 'https://17chigua.com/', name: '首页' } },
          { '@type': 'ListItem', position: 2, item: { '@id': `https://17chigua.com/category/${article.categorySlug}`, name: article.category } },
          { '@type': 'ListItem', position: 3, item: { '@id': `https://17chigua.com/article/${slug}`, name: article.title } },
        ],
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <SEOHead
        title={`${article.title} | 17吃瓜网`}
        description={article.excerpt}
        keywords={article.tags?.join(',') || '17吃瓜网,吃瓜爆料,网红黑料'}
        canonicalUrl={`https://17chigua.com/article/${slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.publishedAt}
        articleAuthor={article.authorName}
        articleTags={article.tags}
      />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-4 border-b border-background-200 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Header - 50/50 Split */}
        <section className="bg-background-50">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-1/2 p-6 md:p-10 flex items-center">
              <div className="rounded-3xl overflow-hidden w-full h-[350px] md:h-[450px]">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 17吃瓜网`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:w-1/2 p-6 md:p-10 flex flex-col justify-center">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-label rounded-full w-fit">
                {article.category}
              </span>
              <h1 className="mt-4 text-2xl md:text-4xl font-heading font-black text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.authorName}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishedAt}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line"></i> {article.category}
                </span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {article.tags?.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 border border-background-200 text-foreground-600 text-xs font-label rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-[800px] mx-auto">
            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {/* Witness Review - Only if there's a comment */}
        {comment && (
          <section className="relative py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-[1100px] mx-auto relative">
              <div className="relative rounded-3xl bg-foreground-950 p-6 md:p-10 flex flex-col lg:flex-row gap-6 md:gap-10">
                <div className="lg:w-[35%] flex-shrink-0 -mt-16 lg:-ml-12 lg:-my-10">
                  <div className="rounded-2xl overflow-hidden h-[280px] md:h-[380px] bg-background-300">
                    <img
                      src={article.imageUrl}
                      alt={`${comment.author}评论文章`}
                      title={`${comment.author} - 辣评`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="lg:w-[65%] flex flex-col justify-center">
                  <span className="text-5xl md:text-7xl font-heading text-accent-400 leading-none mb-4">
                    &ldquo;
                  </span>
                  <blockquote className="text-base md:text-xl text-background-50 leading-relaxed font-body">
                    {comment.comment}
                  </blockquote>
                  <div className="mt-6">
                    <p className="text-sm font-label font-medium text-background-50">{comment.author}</p>
                    <p className="text-xs text-background-50/50">{comment.role}</p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500/20 text-primary-300 font-label text-sm rounded-lg w-fit whitespace-nowrap">
                    目击者辣评 <i className="ri-chat-quote-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
              相关推荐
            </h2>
            <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {relatedArticles.map((rel) => (
                <ArticleCard key={rel.id} article={rel} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}